/**
 * De betaalpagina achter een link uit de mail (SPEC §5.3). Bewust karig:
 * alleen wat nodig is om te betalen, geen persoonsgegevens.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiFout, type Betaalgegevens } from '../api';
import { Datum, Euro, Knop, Kroon, Laden, Melding, useTitel } from '../componenten/basis';
import { Pagina } from '../componenten/Schil';
import { SOORTNAMEN } from '../lib/vormen';

export function Betaalpagina() {
  useTitel('Bijdrage betalen');
  const { token = '' } = useParams();
  const [gegevens, zetGegevens] = useState<Betaalgegevens | null>(null);
  const [fout, zetFout] = useState<string | null>(null);
  const [bezig, zetBezig] = useState(false);

  useEffect(() => {
    api.haal<Betaalgegevens>(`/api/betalen/${encodeURIComponent(token)}`).then(zetGegevens).catch((f: Error) => zetFout(f.message));
  }, [token]);

  async function betaal() {
    zetBezig(true);
    zetFout(null);
    try {
      const uit = await api.stuur<{ checkoutUrl: string }>(`/api/betalen/${encodeURIComponent(token)}/start`);
      window.location.href = uit.checkoutUrl;
    } catch (f) {
      zetFout(f instanceof ApiFout ? f.message : 'Er ging iets mis.');
      zetBezig(false);
    }
  }

  return (
    <Pagina smal>
      <div className="verschijn py-12 sm:py-20">
        {!gegevens && !fout && <Laden />}
        {fout && !gegevens && <Melding soort="fout">{fout}</Melding>}
        {gegevens && (
          <div className="kaart p-8">
            <Kroon className="h-9 w-9 text-goud-400" />
            <h1 className="mt-4 text-3xl">
              {gegevens.status === 'betaald' ? 'Deze bijdrage is al betaald.' : `Je ${SOORTNAMEN[gegevens.soort].toLowerCase()}${gegevens.kalenderjaar ? ` ${gegevens.kalenderjaar}` : ''}`}
            </h1>
            <p className="mt-2 text-inkt-zacht">Voor {gegevens.weergavenaam}.</p>

            <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="etiket">Bedrag</dt>
                <dd className="font-display text-3xl text-goud-300">
                  <Euro cent={gegevens.bedrag_cent} />
                </dd>
              </div>
              {gegevens.kalenderjaar && (
                <div>
                  <dt className="etiket">Heldenjaar</dt>
                  <dd>{gegevens.kalenderjaar}</dd>
                </div>
              )}
            </dl>

            {gegevens.status === 'betaald' && (
              <Melding soort="goed">
                Betaald op <Datum waarde={gegevens.betaald_op} />. Dank je wel.
              </Melding>
            )}

            {(gegevens.status === 'open' || gegevens.status === 'mislukt') && (
              <div className="mt-6 space-y-5">
                {gegevens.status === 'mislukt' && <Melding>De automatische incasso is niet gelukt. Je kunt het hier zelf doen.</Melding>}
                {fout && <Melding soort="fout">{fout}</Melding>}
                {gegevens.onlineMogelijk && (
                  <div>
                    <Knop onClick={betaal} bezig={bezig}>
                      Betaal met iDEAL
                    </Knop>
                    <p className="mt-2 text-xs text-inkt-zacht">Betaal je met iDEAL, dan geef je meteen een machtiging af voor de volgende jaren.</p>
                  </div>
                )}
                {gegevens.rekening && (
                  <div className="rounded-xl border border-lijn bg-nacht p-4 text-sm">
                    <p className="font-medium">Liever zelf overmaken?</p>
                    <p className="mt-1 text-inkt-zacht">
                      <Euro cent={gegevens.bedrag_cent} /> naar <span className="font-mono text-inkt">{gegevens.rekening.iban}</span> t.n.v. {gegevens.rekening.tenaamstelling}, onder vermelding van “Helden - {gegevens.weergavenaam}”. De penningmeester zet hem dan met de hand op betaald.
                    </p>
                  </div>
                )}
              </div>
            )}

            {(gegevens.status === 'verlopen' || gegevens.status === 'kwijtgescholden') && <Melding>Deze bijdrage staat niet meer open.</Melding>}
          </div>
        )}
      </div>
    </Pagina>
  );
}
